// Polymorphic ACL mutations — resource_type + resource_id scoped.
// Backend enforces 7-val ACL polymorphism; FE passes resource_type through
// unchanged. Caller specifies action set; component does not assume action set.
// Debounced batch saves coalesce grid edits into one round-trip — callers
// are expected to debounce before invoking mutate to reduce BE write load.

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { hubCall } from "@/lib/api/hub";
import type { ApiResponse } from "@/types/api.types";
import type { PermissionGroupSummary } from "@/lib/api/services/resource-permission.service";
import type { UserSummary } from "@/types/api.types";

export type PrincipalKind = "user" | "role" | "group";

export interface PermissionEntry {
  principalKind: PrincipalKind;
  principalId?: string;
  principalRole?: string;
  principalLabel?: string;
  principalUser?: UserSummary | null;
  principalGroup?: PermissionGroupSummary | null;
  actions: string[];
  /** Source of this permission — direct or inherited. BE-resolved. */
  source?: "direct" | "folder_inherited";
  sourceResourceType?: string;
  sourceResourceId?: string;
  /** Folder or role name if inherited — for tooltip display. */
  sourceName?: string;
  editable?: boolean;
}

interface UpsertPermissionParams {
  resourceType: string;
  resourceId: string;
  principalKind: PrincipalKind;
  principalId?: string;
  principalRole?: string;
  actions: string[];
}

interface DeletePermissionParams {
  resourceType: string;
  resourceId: string;
  principalKind: PrincipalKind;
  principalId?: string;
  principalRole?: string;
  actions: string[];
}

function principalPayload(params: {
  principalKind: PrincipalKind;
  principalId?: string;
  principalRole?: string;
}) {
  const payload: Record<string, unknown> = { principalKind: params.principalKind };
  if (params.principalKind === "role") {
    payload.principalRole = params.principalRole ?? params.principalId;
  } else {
    payload.principalId = params.principalId;
  }
  return payload;
}

function permissionsKey(resourceType: string, resourceId: string) {
  return ["permissions", resourceType, resourceId];
}

/** Upsert (create or replace) a principal's action set on a resource. */
export function useUpsertPermission() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (params: UpsertPermissionParams): Promise<ApiResponse<{ ok: boolean }>> => {
      return hubCall<ApiResponse<{ ok: boolean }>>(
        "/permissions/grant",
        {
          method: "POST",
          body: JSON.stringify({
            resourceType: params.resourceType,
            resourceId: params.resourceId,
            ...principalPayload(params),
            principalKind: params.principalKind,
            actions: params.actions,
          }),
        }
      );
    },
    onSuccess: (_data, params) => {
      qc.invalidateQueries({
        queryKey: permissionsKey(params.resourceType, params.resourceId),
      });
    },
  });
}

/** Delete a principal's entire permission entry on a resource. */
export function useDeletePermission() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (params: DeletePermissionParams): Promise<ApiResponse<{ ok: boolean }>> => {
      return hubCall<ApiResponse<{ ok: boolean }>>(
        "/permissions/revoke",
        {
          method: "POST",
          body: JSON.stringify({
            resourceType: params.resourceType,
            resourceId: params.resourceId,
            ...principalPayload(params),
            actions: params.actions,
          }),
        }
      );
    },
    onSuccess: (_data, params) => {
      qc.invalidateQueries({
        queryKey: permissionsKey(params.resourceType, params.resourceId),
      });
    },
  });
}
